/**
 * Binance public WebSocket mini-ticker stream for BTC/ETH live prices.
 * Reconnects with backoff; after repeated failures the consumer should
 * fall back to REST polling (fetchSimplePrice). Ticks are throttled to
 * one update per second by the caller (useTicker hook usage in App).
 */
import { WS_URL, WS_COINS, INTERVALS } from "../utils/constants";
import { fetchSimplePrice } from "./coingecko";

const STREAM_MAP = {};
WS_COINS.forEach((c) => {
  STREAM_MAP[`${c.stream}@miniTicker`] = c.id;
});

/**
 * Computes 24h change % from a miniTicker payload ({ c, o, h, l }).
 */
export function computeTicker(tick) {
  const close = Number(tick.c);
  const open = Number(tick.o);
  const change = open ? ((close - open) / open) * 100 : 0;
  return { price: close, change };
}

/**
 * Opens the combined WS stream and wires callbacks.
 * Returns a cleanup function.
 *
 * opts:
 *   onUpdate({ bitcoin: {price, change}, ethereum: {...} })
 *   onStatus("connecting" | "live" | "failed")
 */
export function connectTicker({ onUpdate, onStatus }) {
  let ws = null;
  let closedByUs = false;
  let failures = 0;
  let retryTimer = null;
  let fallbackTimer = null;
  let last = null; // throttled outbound state
  let flushTimer = null;

  const emit = (state) => {
    const now = Date.now();
    if (!last || now - last.time >= INTERVALS.tickerThrottleMs) {
      last = { time: now, state };
      onUpdate(state);
    } else if (!flushTimer) {
      flushTimer = setTimeout(() => {
        flushTimer = null;
        if (last && last.state === state) return;
        last = { time: Date.now(), state };
        onUpdate(state);
      }, INTERVALS.tickerThrottleMs);
    }
  };

  const startFallback = () => {
    if (fallbackTimer) return;
    onStatus("fallback");
    const poll = async () => {
      try {
        const data = await fetchSimplePrice({
          ids: WS_COINS.map((c) => c.id),
          currency: "usd",
        });
        const state = {};
        WS_COINS.forEach((c) => {
          const d = data?.[c.id];
          if (d) {
            state[c.id] = {
              price: d.usd,
              change: d.usd_24h_change ?? 0,
            };
          }
        });
        onUpdate(state);
      } catch {
        /* silent — banner layer owns error UX */
      }
    };
    poll();
    fallbackTimer = setInterval(poll, INTERVALS.fallbackPollMs);
  };

  const stopFallback = () => {
    if (fallbackTimer) clearInterval(fallbackTimer);
    fallbackTimer = null;
  };

  const scheduleReconnect = () => {
    if (closedByUs) return;
    failures += 1;
    if (failures > INTERVALS.binanceMaxFailures) {
      onStatus("failed");
      startFallback();
      return;
    }
    const delay = Math.min(
      INTERVALS.binanceRetryBaseMs * 2 ** (failures - 1),
      INTERVALS.binanceRetryMaxMs
    );
    retryTimer = setTimeout(open, delay);
  };

  const open = () => {
    onStatus("connecting");
    try {
      ws = new WebSocket(WS_URL);
    } catch {
      scheduleReconnect();
      return;
    }

    ws.onopen = () => {
      failures = 0;
      stopFallback();
      onStatus("live");
    };

    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        const coinId = STREAM_MAP[msg.stream];
        if (!coinId || !msg.data) return;
        const { price, change } = computeTicker(msg.data);
        last = null; // allow immediate emit for fresh stream data
        emit({ [coinId]: { price, change } });
      } catch {
        /* ignore malformed frames */
      }
    };

    ws.onclose = scheduleReconnect;
    ws.onerror = () => {
      try {
        ws.close();
      } catch {
        /* noop */
      }
    };
  };

  open();

  return function cleanup() {
    closedByUs = true;
    if (retryTimer) clearTimeout(retryTimer);
    stopFallback();
    if (flushTimer) clearTimeout(flushTimer);
    if (ws) {
      ws.onclose = null;
      ws.onmessage = null;
      ws.onerror = null;
      try {
        ws.close();
      } catch {
        /* noop */
      }
    }
  };
}
